import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useLang } from "../contexts/LangContext";

export default function Header() {
  const { t, lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeHash, setActiveHash] = useState("#top");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Active section: scrollspy on home, pathname match on sub-pages
  useEffect(() => {
    const pathMap = {
      "/ueber-uns": "#ueber-uns",
      "/catering": "#leistungen",
      "/galerie": "#galerie",
      "/kontakt": "#kontakt",
    };
    if (location.pathname !== "/") {
      setActiveHash(pathMap[location.pathname] || "");
      return;
    }
    const ids = ["top", "ueber-uns", "leistungen", "galerie", "kontakt"];
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveHash("#" + visible[0].target.id);
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: [0, 0.25, 0.5, 1] }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [location.pathname]);

  const sections = [
    { hash: "#top", label: t.nav.home, id: "nav-home" },
    { hash: "#ueber-uns", label: t.nav.about, id: "nav-about" },
    { hash: "#leistungen", label: t.nav.services, id: "nav-services" },
    { hash: "#galerie", label: t.nav.gallery, id: "nav-gallery" },
    { hash: "#kontakt", label: t.nav.contact, id: "nav-contact" },
  ];

  const goTo = (hash) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + hash);
      return;
    }
    if (hash === "#top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.querySelector(hash);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 70;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[hsl(var(--background))]/85 border-b border-border" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-link" className="flex items-center leading-none" aria-label="Tæste — zur Startseite">
          <span className="sr-only">Tæste</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {sections.map((s) => {
            const isActive = activeHash === s.hash;
            return (
              <button
                key={s.hash}
                onClick={() => goTo(s.hash)}
                data-testid={`${s.id}-link`}
                aria-current={isActive ? "true" : undefined}
                className={`relative text-sm tracking-wide transition-colors pb-1 ${
                  isActive
                    ? "text-[hsl(var(--foreground))] font-semibold after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-px after:bg-[hsl(var(--primary))]"
                    : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center text-xs tracking-widest uppercase">
            <button
              data-testid="lang-switch-de"
              onClick={() => setLang("de")}
              className={`px-2 py-1 ${lang === "de" ? "text-[hsl(var(--foreground))] font-semibold" : "text-[hsl(var(--muted-foreground))]"}`}
              aria-pressed={lang === "de"}
            >
              DE
            </button>
            <span className="text-[hsl(var(--muted-foreground))]">·</span>
            <button
              data-testid="lang-switch-en"
              onClick={() => setLang("en")}
              className={`px-2 py-1 ${lang === "en" ? "text-[hsl(var(--foreground))] font-semibold" : "text-[hsl(var(--muted-foreground))]"}`}
              aria-pressed={lang === "en"}
            >
              EN
            </button>
          </div>
          <button
            onClick={() => goTo("#kontakt")}
            data-testid="header-cta-button"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 text-xs tracking-[0.18em] uppercase font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--accent))] transition-colors rounded-sm"
          >
            {t.nav.cta}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[hsl(var(--background))] border-t border-border">
          <div className="px-6 py-6 flex flex-col gap-4">
            {sections.map((s) => {
              const isActive = activeHash === s.hash;
              return (
                <button
                  key={s.hash}
                  onClick={() => goTo(s.hash)}
                  data-testid={`${s.id}-mobile-link`}
                  aria-current={isActive ? "true" : undefined}
                  className={`text-base text-left ${
                    isActive
                      ? "text-[hsl(var(--foreground))] font-semibold underline underline-offset-4 decoration-[hsl(var(--primary))]"
                      : "text-[hsl(var(--muted-foreground))]"
                  }`}
                >
                  {s.label}
                </button>
              );
            })}
            <div className="pt-3 mt-2 border-t border-border flex flex-col gap-3">
              <Link
                to="/impressum"
                data-testid="mobile-imprint-link"
                onClick={() => setOpen(false)}
                className="text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
              >
                {t.footer.imprint}
              </Link>
              <Link
                to="/datenschutz"
                data-testid="mobile-privacy-link"
                onClick={() => setOpen(false)}
                className="text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
              >
                {t.footer.privacy}
              </Link>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button data-testid="lang-switch-de-mobile" onClick={() => setLang("de")} className={`px-3 py-1 text-xs tracking-widest uppercase ${lang === "de" ? "font-semibold" : ""}`}>DE</button>
              <span>·</span>
              <button data-testid="lang-switch-en-mobile" onClick={() => setLang("en")} className={`px-3 py-1 text-xs tracking-widest uppercase ${lang === "en" ? "font-semibold" : ""}`}>EN</button>
            </div>
            <button onClick={() => goTo("#kontakt")} data-testid="mobile-cta" className="mt-2 inline-flex justify-center items-center px-5 py-3 text-xs tracking-[0.18em] uppercase font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] rounded-sm">
              {t.nav.cta}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
